import React, { useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';

import { A11yWrapper } from 'components/a11y-wrapper';
import Currency from 'components/currency';
import { Box } from 'components/ui/box';
import { ServiceMode } from 'generated/graphql-gateway';
import { CurrencyLineSkeleton } from 'pages/cart/currency-line-skeleton';
import { DeliveryFeesModal } from 'pages/cart/delivery-fees-modal';
import { IconContainer } from 'pages/cart/your-cart/styled';
import { t } from 'state/intl/translations';
import { spacing } from 'styles/constants';
import { hiddenAccessibilityPlatformProps } from 'utils/accessibility';
import { computeDeliveryFee } from 'utils/cart/helper';
import {
  geographicalFeeLabelIdMap,
  isMappedGeographicalFeeState,
} from 'utils/order/geographical-fee';

import DeliveryFees from './delivery-fees';
import { Total, TotalLabel, TotalLabelContainer, TotalsRow } from './styled';
import { TaxesRow } from './taxes-row';

interface TaxesAndFeesProps {
  deliveryFee: number | null;
  deliveryFeeDiscount: number | null;
  dropoffState?: string | null;
  serviceFeeCents: number;
  smallCartFeeCents: number;
  geographicalFeeCents: number;
  taxCents: number;
  serviceMode: ServiceMode | null;
  subTotalCents: number;
  isLoading?: boolean;
}

const TaxesAndFees: React.FC<React.PropsWithChildren<TaxesAndFeesProps>> = ({
  isLoading,
  deliveryFee,
  deliveryFeeDiscount,
  dropoffState,
  serviceFeeCents,
  smallCartFeeCents,
  geographicalFeeCents,
  taxCents,
  serviceMode,
  subTotalCents,
}) => {
  const [showDeliveryFeesModal, setShowDeliveryFeesModal] = useState(false);

  const finalDeliveryFee = computeDeliveryFee({
    feeCents: deliveryFee,
    feeDiscountCents: deliveryFeeDiscount,
    serviceFeeCents,
    smallCartFeeCents,
    geographicalFeeCents,
  });

  const isFreeDelivery = finalDeliveryFee === 0 && !isLoading;
  const isSingleDeliveryFee = !serviceFeeCents && !smallCartFeeCents && !geographicalFeeCents;

  // Use a custom label for geographical delivery fees as required
  const geographicalFeeLabelId = isMappedGeographicalFeeState(dropoffState)
    ? geographicalFeeLabelIdMap[dropoffState]
    : 'geographicalFee';

  const onPressDeliveryFees = () => {
    setShowDeliveryFeesModal(true);
  };

  return (
    <>
      <TotalsRow>
        {isLoading ? (
          <CurrencyLineSkeleton />
        ) : isSingleDeliveryFee ? (
          <>
            <A11yWrapper>
              <TotalLabel>{t('deliveryFee')}</TotalLabel>
            </A11yWrapper>
            <Total testID="delivery-fee-amount">
              <Currency amount={finalDeliveryFee} textProps={{ fontSize: 'md' }} />
            </Total>
          </>
        ) : (
          <TotalLabelContainer>
            <TotalLabel>{t('deliveryFees')}</TotalLabel>
            <IconContainer
              accessibilityLabel={t('taxesAndFeesButtonAriaLabel')}
              onPress={onPressDeliveryFees}
            >
              <Icon variant="info" size="$5" {...hiddenAccessibilityPlatformProps} />
            </IconContainer>
          </TotalLabelContainer>
        )}
      </TotalsRow>
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <Box style={styles.feeBreakdown}>
          <TotalsRow accessible>
            <TotalLabel isFreeDelivery={isFreeDelivery}>{t('deliveryFee')}</TotalLabel>
            <DeliveryFees
              isFreeDelivery={isFreeDelivery}
              deliveryFee={finalDeliveryFee}
              deliveryFeeDiscount={deliveryFeeDiscount}
              isLoading={isLoading}
            />
          </TotalsRow>
          {geographicalFeeCents > 0 && (
            <TotalsRow accessible>
              <TotalLabel>{t(geographicalFeeLabelId)}</TotalLabel>
              <Total testID="geographical-fee-amount">
                <Currency amount={geographicalFeeCents} textProps={{ fontSize: 'md' }} />
              </Total>
            </TotalsRow>
          )}
          {serviceFeeCents > 0 && (
            <TotalsRow accessible>
              <TotalLabel>{t('serviceFee')}</TotalLabel>
              <Total testID="service-fee-amount">
                <Currency amount={serviceFeeCents} textProps={{ fontSize: 'md' }} />
              </Total>
            </TotalsRow>
          )}
          {smallCartFeeCents > 0 && (
            <TotalsRow accessible>
              {isLoading ? (
                <CurrencyLineSkeleton />
              ) : (
                <>
                  <TotalLabel>{t('smallCartFee')}</TotalLabel>
                  <Total testID="small-cart-fee-amount">
                    <Currency amount={smallCartFeeCents} textProps={{ fontSize: 'md' }} />
                  </Total>
                </>
              )}
            </TotalsRow>
          )}
        </Box>
      )}
      {isLoading ? (
        <CurrencyLineSkeleton />
      ) : (
        <TaxesRow textProps={{ fontSize: 'md' }} taxCents={taxCents} isLoading={isLoading} />
      )}
      <DeliveryFeesModal
        open={showDeliveryFeesModal}
        onClose={() => setShowDeliveryFeesModal(false)}
        serviceMode={serviceMode}
        subTotalCents={subTotalCents}
        deliveryFeeCents={finalDeliveryFee}
        serviceFeeCents={serviceFeeCents}
        geographicalFeeCents={geographicalFeeCents}
        smallCartFeeCents={smallCartFeeCents}
        isLoading={isLoading}
      />
    </>
  );
};

export default TaxesAndFees;

const styles = StyleSheet.create({
  feeBreakdown: {
    paddingLeft: spacing.$4,
  },
});
